import { Box, Text } from '@mantine/core';
import type { ReactElement, ReactNode } from 'react';
import { type VizAccent } from '@/design/VizAccents';

interface InsightsScoreRingProps {
    /** Score in [0,100], drawn as ring fill. */
    score: number;
    /** Used for the accessible label; rendered as the caption unless `sublabel` is given. */
    label: string;
    /** @deprecated The ring is brand-coloured now; accent is ignored. */
    accent?: VizAccent;
    /** Ring colour override (e.g. an adoption maturity-band colour); defaults to the brand accent. */
    color?: string;
    size?: number;
    /** Custom caption below the number (e.g. a trend delta) — replaces the plain `label` text. */
    sublabel?: ReactNode;
}

/** SVG donut ring for the 0–100 composite score, number centered. */
export default function InsightsScoreRing({ score, label, color, size = 148, sublabel }: InsightsScoreRingProps): ReactElement {
    const tokens = { solid: color ?? 'var(--bm-color-brand-accent)' };
    const stroke = 11;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const clamped = Math.max(0, Math.min(100, score));
    const filled = (clamped / 100) * circumference;
    return (
        <Box pos="relative" w={size} h={size}>
            <svg width={size} height={size} role="img" aria-label={`${label}: ${clamped} out of 100`}>
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--bm-color-border-subtle)" strokeWidth={stroke} />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={tokens.solid}
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={`${filled} ${circumference - filled}`}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
            <Box pos="absolute" top={0} left={0} w={size} h={size} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Text fw={700} ff="monospace" style={{ fontSize: '34px', lineHeight: 1 }}>
                    {clamped}
                </Text>
                <Box mt={5}>
                    {sublabel ?? (
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                            {label}
                        </Text>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
